import { Link } from 'react-router-dom';
import { ShieldAlert, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { useAuth } from '@/context/AuthContext.js';

interface UnauthorizedPageProps {
  message?: string;
  requiredRoles?: string[];
}

export function UnauthorizedPage({ message, requiredRoles }: UnauthorizedPageProps) {
  const { user } = useAuth();
  const currentRole = user?.role || 'guest';

  return (
    <div className="flex h-full min-h-[60vh] flex-col items-center justify-center p-6 text-center select-none">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 border border-red-200">
        <ShieldAlert className="h-8 w-8 text-red-600" />
      </div>

      <div className="space-y-2 max-w-md">
        <div className="inline-block rounded-full bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-red-800">
          HTTP 403 • Access Denied
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-brand-navy">
          Not Authorized
        </h1>
        <p className="text-sm text-slate-600">
          {message || 'You do not have the required permissions to access this section of Techosolution Project OS.'}
        </p>

        <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 text-left">
          <div className="flex justify-between py-1 border-b border-slate-100">
            <span className="font-semibold text-slate-500">Your Current Role:</span>
            <span className="font-mono font-bold capitalize text-brand-navy">{currentRole.replace('_', ' ')}</span>
          </div>
          {requiredRoles && requiredRoles.length > 0 && (
            <div className="flex justify-between py-1 pt-2">
              <span className="font-semibold text-slate-500">Allowed Role(s):</span>
              <span className="font-mono text-slate-700 capitalize">
                {requiredRoles.map((r) => r.replace('_', ' ')).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Button asChild variant="default" className="bg-brand-teal hover:bg-teal-700 text-white">
          <Link to="/my-tasks" className="flex items-center space-x-1">
            <ArrowLeft className="h-4 w-4" />
            <span>Return to My Tasks</span>
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/dashboard">Go to Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}

export default UnauthorizedPage;
